import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../auth-lib';
import { Button, Card, Modal, StatusPill, useToast } from '../../design-system';
import { statusLabel } from '../../lib/taskStatus';

const taskRef = (n) => (n != null ? `TASK-${String(n).padStart(4, '0')}` : 'Task');

// "Blocked by" — the tasks that must be completed before this one can be.
//
// The real sequence inside a filing period is books -> audit -> return, where
// starting out of order wastes work. The server refuses to complete a task with
// open blockers (and refuses cycles), so this card's job is to make that
// refusal predictable: you can see what you're waiting on before you try.
function TaskDependenciesCard({ task, setTask, taskId, locked }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const blockers = task.blockedBy || [];
  const openBlockers = blockers.filter((b) => b.status !== 'completed');

  useEffect(() => {
    if (!open) return;
    setSelected(blockers.map((b) => b._id || b));
    // Only open tasks are useful as blockers, and the list must exclude this
    // task itself — picking it would be an immediate self-cycle.
    apiClient
      .get('/api/tasks', { params: { sort: 'dueDate' } })
      .then((res) => setCandidates((res.data || []).filter((t) => t._id !== taskId)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates.slice(0, 50);
    return candidates
      .filter(
        (t) =>
          t.title?.toLowerCase().includes(q) ||
          taskRef(t.taskNumber).toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [candidates, search]);

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiClient.patch(`/api/tasks/${taskId}`, { blockedBy: selected });
      setTask(res.data);
      setOpen(false);
      toast.success('Dependencies updated');
    } catch (err) {
      // The server refuses cycles and dangling references with a 409 and an
      // explanatory message — surface it rather than a generic failure.
      toast.danger(err.response?.data?.error || 'Failed to update dependencies');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card padding="md" className="td-deps-card">
      <div className="td-card-head">
        <span className="td-card-title">
          Blocked by {blockers.length > 0 && <span className="td-count">{openBlockers.length} open</span>}
        </span>
        {!locked && (
          <button type="button" className="td-add-link" onClick={() => setOpen(true)}>
            {blockers.length ? 'Edit' : '+ Add'}
          </button>
        )}
      </div>

      {blockers.length === 0 ? (
        <div className="td-muted">Nothing is blocking this task.</div>
      ) : (
        <>
          {openBlockers.length > 0 && (
            <div className="td-deps-warning">
              🔒 This task can&apos;t be completed until {openBlockers.length === 1 ? 'it' : 'they'} finish.
            </div>
          )}
          <div className="td-deps-list">
            {blockers.map((b) => (
              <div key={b._id} className={`td-dep-row ${b.status === 'completed' ? 'is-done' : ''}`}>
                <Link to={`/org-admin/tasks/${b._id}`} className="td-dep-link">
                  <span className="td-dep-ref">{taskRef(b.taskNumber)}</span>
                  <span className="td-dep-title">{b.title}</span>
                </Link>
                <StatusPill status={b.status} label={statusLabel(b.status)} />
              </div>
            ))}
          </div>
        </>
      )}

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Which tasks block this one?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={saving}>Save</Button>
          </>
        }
      >
        <p className="td-muted td-deps-hint">
          This task can&apos;t be marked complete until every task picked here is complete.
          Circular chains are refused.
        </p>
        <input
          className="td-inline-input td-deps-search"
          placeholder="🔎 Search tasks…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="td-deps-picker">
          {filtered.length === 0 ? (
            <div className="td-muted">No matching tasks</div>
          ) : (
            filtered.map((t) => (
              <label key={t._id} className="td-dep-option">
                <input
                  type="checkbox"
                  checked={selected.includes(t._id)}
                  onChange={() => toggle(t._id)}
                />
                <span className="td-dep-ref">{taskRef(t.taskNumber)}</span>
                <span className="td-dep-title">{t.title}</span>
                <StatusPill status={t.status} label={statusLabel(t.status)} />
              </label>
            ))
          )}
        </div>
      </Modal>
    </Card>
  );
}

export default TaskDependenciesCard;
